import { useState, useEffect, useCallback, useRef } from "react";
import { ActivityFeedRepository } from "@/repositories/activityFeed";
import { ActivityFeedUnion, ActivityType } from "@/types/v2";
import { DocumentSnapshot } from "firebase/firestore";

export interface ActivityFeedSearchFilters {
  activityType?: ActivityType | "all";
  cardNumber?: string;
  startDate?: Date;
  endDate?: Date;
}

export const useActivityFeedPagination = (shopId: string | undefined) => {
  const [activities, setActivities] = useState<ActivityFeedUnion[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastVisible, setLastVisible] = useState<
    DocumentSnapshot | undefined
  >();
  const [filters, setFilters] = useState<ActivityFeedSearchFilters>({});
  const [pageSize] = useState(20);

  // Track the first item ID to detect when new activities are added
  const firstItemIdRef = useRef<string | null>(null);
  // Track current page count for position preservation
  const currentPageCountRef = useRef<number>(1);
  // Keep current filters in ref for subscription access
  const filtersRef = useRef<ActivityFeedSearchFilters>({});

  const loadActivities = useCallback(
    async (
      isInitialLoad = false,
      searchFilters: ActivityFeedSearchFilters = {}
    ) => {
      if (!shopId || loading) return;

      setLoading(true);
      try {
        const activityType =
          searchFilters.activityType === "all"
            ? undefined
            : searchFilters.activityType;

        const result = await ActivityFeedRepository.getPaginatedActivities(
          shopId,
          pageSize,
          isInitialLoad ? undefined : lastVisible,
          activityType,
          searchFilters.cardNumber,
          searchFilters.startDate,
          searchFilters.endDate
        );

        if (isInitialLoad) {
          setActivities(result.docs);
          // Update the first item ID reference when doing initial load
          firstItemIdRef.current = result.docs[0]?.id || null;
        } else {
          setActivities((prev) => [...prev, ...result.docs]);
        }

        setHasMore(result.hasMore);
        setLastVisible(result.lastVisible);
      } catch (error) {
        console.error("Error loading activities:", error);
      } finally {
        setLoading(false);
      }
    },
    [shopId, loading, lastVisible, pageSize]
  );

  const applyFilters = useCallback(
    (newFilters: ActivityFeedSearchFilters) => {
      setFilters(newFilters);
      filtersRef.current = newFilters;
      setLastVisible(undefined);
      currentPageCountRef.current = 1;
      loadActivities(true, newFilters);
    },
    [loadActivities]
  );

  const loadMore = useCallback(() => {
    if (hasMore && !loading) {
      currentPageCountRef.current += 1;
      loadActivities(false, filters);
    }
  }, [hasMore, loading, loadActivities, filters]);

  // Function to reload activities up to a specific page count
  const loadActivitiesToPage = useCallback(
    async (
      targetPageCount: number,
      searchFilters: ActivityFeedSearchFilters = {}
    ) => {
      if (!shopId || loading) return;

      setLoading(true);
      try {
        const activityType =
          searchFilters.activityType === "all"
            ? undefined
            : searchFilters.activityType;

        const totalItemsToLoad = pageSize * targetPageCount;

        const result = await ActivityFeedRepository.getPaginatedActivities(
          shopId,
          totalItemsToLoad,
          undefined, // Start from beginning
          activityType,
          searchFilters.cardNumber,
          searchFilters.startDate,
          searchFilters.endDate
        );

        setActivities(result.docs);
        firstItemIdRef.current = result.docs[0]?.id || null;
        setHasMore(result.hasMore);
        setLastVisible(result.lastVisible);
        currentPageCountRef.current = targetPageCount;
      } catch (error) {
        console.error("Error loading activities to page:", error);
      } finally {
        setLoading(false);
      }
    },
    [shopId, loading, pageSize]
  );

  const resetFilters = useCallback(() => {
    const emptyFilters = {};
    setFilters(emptyFilters);
    filtersRef.current = emptyFilters;
    setLastVisible(undefined);
    currentPageCountRef.current = 1;
    loadActivities(true, emptyFilters);
  }, [loadActivities]);

  // Initial load when shopId changes
  useEffect(() => {
    if (shopId) {
      setActivities([]);
      setLastVisible(undefined);
      setHasMore(true);
      currentPageCountRef.current = 1;
      filtersRef.current = filters;
      loadActivities(true, filters);
    }
  }, [shopId, filters]);

  // Subscribe to real-time activity feed changes to detect new items
  useEffect(() => {
    if (!shopId) return;

    const unsubscribe = ActivityFeedRepository.subscribeAllUnion(
      shopId,
      (latestActivities) => {
        if (!latestActivities || latestActivities.length === 0) return;

        const currentFirstItemId = latestActivities[0]?.id;

        // If the first item has changed and we have existing activities, refetch to current page
        if (
          firstItemIdRef.current !== null &&
          currentFirstItemId !== firstItemIdRef.current
        ) {
          // Reload up to current page to preserve position
          loadActivitiesToPage(currentPageCountRef.current, filtersRef.current);
        }
      },
      15
    );

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [shopId]);

  return {
    activities,
    loading,
    hasMore,
    filters,
    applyFilters,
    loadMore,
    resetFilters,
  };
};
